import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import { Plus, Edit, Trash2, PlayCircle, Repeat, Target } from "lucide-react";

const EXPENSE_CATEGORIES = [
  { value: 'rent', label: 'Rent & Facilities' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'employee', label: 'Employee Costs' },
  { value: 'software', label: 'Software' },
  { value: 'tools', label: 'Tools' },
  { value: 'office_supplies', label: 'Office Supplies' },
  { value: 'maintenance', label: 'Equipment Maintenance' },
  { value: 'operational', label: 'Operational' },
  { value: 'other', label: 'Other' }
];

const categoryLabel = (value) => EXPENSE_CATEGORIES.find(c => c.value === value)?.label || value;

const emptyForm = {
  name: '', category: 'rent', amount: '', payment_method: 'cash',
  bank_account_id: '', day_of_month: '1', vendor_name: '', notes: ''
};

const RecurringExpensesSection = () => {
  const [recurring, setRecurring] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [budgetMonth, setBudgetMonth] = useState(new Date().toISOString().slice(0, 7));
  const [budgetComparison, setBudgetComparison] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ id: null, category: 'rent', monthly_amount: '' });

  const loadRecurring = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recurring-expenses`);
      setRecurring(res.data || []);
    } catch (e) {
      console.error('Error loading recurring expenses:', e);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/bank-accounts`);
      setBankAccounts(res.data || []);
    } catch (e) {
      console.error('Error loading bank accounts:', e);
    }
  }, []);

  const loadBudgetComparison = useCallback(async (month) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/budgets/comparison?month=${month}`);
      setBudgetComparison(res.data?.results || []);
    } catch (e) {
      console.error('Error loading budget comparison:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadRecurring(), loadBankAccounts(), loadBudgetComparison(budgetMonth)]);
      setLoading(false);
    })();
  }, [loadRecurring, loadBankAccounts, loadBudgetComparison, budgetMonth]);

  const openAddForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name,
      category: item.category,
      amount: item.amount.toString(),
      payment_method: item.payment_method,
      bank_account_id: item.bank_account_id || '',
      day_of_month: item.day_of_month.toString(),
      vendor_name: item.vendor_name || '',
      notes: item.notes || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.amount) {
      toast.error('Please enter a name and amount');
      return;
    }
    if (formData.payment_method === 'bank_account' && !formData.bank_account_id) {
      toast.error('Please select a bank account');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        category: formData.category,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        bank_account_id: formData.payment_method === 'bank_account' ? formData.bank_account_id : null,
        day_of_month: parseInt(formData.day_of_month, 10),
        vendor_name: formData.vendor_name.trim(),
        notes: formData.notes.trim()
      };
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/recurring-expenses/${editingId}`, payload);
        toast.success('Recurring expense updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/recurring-expenses`, payload);
        toast.success('Recurring expense created');
      }
      setShowForm(false);
      loadRecurring();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save recurring expense');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${BACKEND_URL}/api/recurring-expenses/${item.id}`, { is_active: !item.is_active });
      toast.success(item.is_active ? 'Paused' : 'Resumed');
      loadRecurring();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recurring expense? This will not affect past postings.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/recurring-expenses/${id}`);
      toast.success('Recurring expense deleted');
      loadRecurring();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRunNow = async (item) => {
    if (!window.confirm(`Post ₹${item.amount.toLocaleString()} for "${item.name}" to accounting now?`)) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/recurring-expenses/${item.id}/run-now`);
      toast.success(res.data?.message || 'Posted successfully');
      loadRecurring();
      loadBudgetComparison(budgetMonth);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post expense');
    }
  };

  const openBudgetForm = (category, existing) => {
    setBudgetForm({ id: budgetIdByCategory[category] || null, category, monthly_amount: existing?.budgeted ? existing.budgeted.toString() : '' });
    setShowBudgetForm(true);
  };

  const handleBudgetSubmit = async (e) => {
    e.preventDefault();
    if (!budgetForm.monthly_amount) {
      toast.error('Enter a monthly budget amount');
      return;
    }
    try {
      if (budgetForm.id) {
        await axios.put(`${BACKEND_URL}/api/budgets/${budgetForm.id}`, { monthly_amount: parseFloat(budgetForm.monthly_amount) });
      } else {
        await axios.post(`${BACKEND_URL}/api/budgets`, { category: budgetForm.category, monthly_amount: parseFloat(budgetForm.monthly_amount) });
      }
      toast.success('Budget saved');
      setShowBudgetForm(false);
      loadBudgetComparison(budgetMonth);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save budget');
    }
  };

  // Need budget IDs for editing - refetch list of budgets to map category -> id
  const [budgetIdByCategory, setBudgetIdByCategory] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/budgets`);
        const map = {};
        (res.data || []).forEach(b => { map[b.category] = b.id; });
        setBudgetIdByCategory(map);
      } catch (e) { /* silent */ }
    })();
  }, [showBudgetForm, budgetComparison]);

  return (
    <div className="space-y-8" data-testid="recurring-budget-section">
      {/* Recurring Expenses */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Repeat className="h-5 w-5 text-orange-500" /> Recurring Expenses</CardTitle>
            <CardDescription>Rent, utilities, and other recurring bills — auto-posted to accounting on their scheduled day each month.</CardDescription>
          </div>
          <Button onClick={openAddForm} data-testid="add-recurring-expense-button">
            <Plus className="h-4 w-4 mr-2" /> Add Recurring Expense
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : recurring.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="no-recurring-expenses">No recurring expenses set up yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Amount</th>
                    <th className="py-2 pr-3">Method</th>
                    <th className="py-2 pr-3">Next Run</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recurring.map((item) => (
                    <tr key={item.id} className="border-b last:border-0" data-testid={`recurring-expense-row-${item.id}`}>
                      <td className="py-2 pr-3 font-medium">{item.name}</td>
                      <td className="py-2 pr-3"><Badge variant="outline">{categoryLabel(item.category)}</Badge></td>
                      <td className="py-2 pr-3">₹{item.amount.toLocaleString()}</td>
                      <td className="py-2 pr-3 capitalize">{item.payment_method.replace('_', ' ')}</td>
                      <td className="py-2 pr-3">{item.next_run_date} <span className="text-gray-400">(day {item.day_of_month})</span></td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <Switch checked={item.is_active} onCheckedChange={() => toggleActive(item)} data-testid={`recurring-expense-toggle-${item.id}`} />
                          <span className={item.is_active ? 'text-green-600' : 'text-gray-400'}>{item.is_active ? 'Active' : 'Paused'}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleRunNow(item)} title="Run now" data-testid={`recurring-expense-run-now-${item.id}`}>
                            <PlayCircle className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditForm(item)} data-testid={`recurring-expense-edit-${item.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} data-testid={`recurring-expense-delete-${item.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Budget vs Actual */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-orange-500" /> Monthly Budget vs Actual</CardTitle>
            <CardDescription>Set an expected monthly spend per category and track actual expenses against it.</CardDescription>
          </div>
          <Input
            type="month"
            value={budgetMonth}
            onChange={(e) => setBudgetMonth(e.target.value)}
            className="w-40"
            data-testid="budget-month-picker"
          />
        </CardHeader>
        <CardContent>
          {budgetComparison.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="no-budgets">No budgets or expenses recorded for this month yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {budgetComparison.map((row) => {
                const hasBudget = row.percent_used !== null && row.percent_used !== undefined;
                const over = hasBudget && row.actual > row.budgeted;
                return (
                  <div key={row.category} className="border rounded-lg p-4" data-testid={`budget-row-${row.category}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">{categoryLabel(row.category)}</p>
                        <p className="text-xs text-gray-500">
                          ₹{row.actual.toLocaleString()} spent{hasBudget ? ` of ₹${row.budgeted.toLocaleString()} budgeted` : ' (no budget set)'}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openBudgetForm(row.category, row)} data-testid={`budget-set-${row.category}`}>
                        {hasBudget ? 'Edit' : 'Set Budget'}
                      </Button>
                    </div>
                    {hasBudget && (
                      <>
                        <Progress value={Math.min(row.percent_used, 100)} />
                        <p className={`text-xs mt-1 ${over ? 'text-red-600' : 'text-gray-500'}`}>
                          {row.percent_used}% used {over ? `— ₹${Math.abs(row.variance).toLocaleString()} over budget` : `— ₹${row.variance.toLocaleString()} remaining`}
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Recurring Expense Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Recurring Expense' : 'Add Recurring Expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="rec-name">Name *</Label>
              <Input id="rec-name" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Office Rent" data-testid="recurring-expense-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="recurring-expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rec-amount">Amount (₹) *</Label>
                <Input id="rec-amount" type="number" min="0" step="0.01" value={formData.amount}
                  onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))} data-testid="recurring-expense-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger data-testid="recurring-expense-payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_account">Bank Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rec-day">Day of Month (1-28) *</Label>
                <Input id="rec-day" type="number" min="1" max="28" value={formData.day_of_month}
                  onChange={(e) => setFormData(p => ({ ...p, day_of_month: e.target.value }))} data-testid="recurring-expense-day-input" />
              </div>
            </div>
            {formData.payment_method === 'bank_account' && (
              <div>
                <Label>Select Bank Account *</Label>
                <Select value={formData.bank_account_id} onValueChange={(v) => setFormData(p => ({ ...p, bank_account_id: v }))}>
                  <SelectTrigger data-testid="recurring-expense-bank-account-select"><SelectValue placeholder="Choose bank account" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.bank_name} - **** {a.account_number?.slice(-4)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="rec-vendor">Vendor Name</Label>
              <Input id="rec-vendor" value={formData.vendor_name} onChange={(e) => setFormData(p => ({ ...p, vendor_name: e.target.value }))}
                placeholder="e.g. Landlord, Electricity Board" data-testid="recurring-expense-vendor-input" />
            </div>
            <div>
              <Label htmlFor="rec-notes">Notes</Label>
              <Textarea id="rec-notes" value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                data-testid="recurring-expense-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="recurring-expense-submit-button">
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set/Edit Budget Dialog */}
      <Dialog open={showBudgetForm} onOpenChange={setShowBudgetForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{budgetForm.id ? 'Edit Budget' : 'Set Budget'} — {categoryLabel(budgetForm.category)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBudgetSubmit} className="space-y-4">
            <div>
              <Label htmlFor="budget-amount">Monthly Budget Amount (₹) *</Label>
              <Input id="budget-amount" type="number" min="0" step="0.01" value={budgetForm.monthly_amount}
                onChange={(e) => setBudgetForm(p => ({ ...p, monthly_amount: e.target.value }))} data-testid="budget-amount-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBudgetForm(false)}>Cancel</Button>
              <Button type="submit" data-testid="budget-submit-button">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringExpensesSection;
